import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { formatAmount, sanitizeAmountInput } from "../helper";

/*
 * The ONE amount field (2026-10-22). A drop-in for <Form.Control> wherever money is typed or shown
 * in a field.
 *
 *  - Text field (inputMode "decimal" brings up the number pad on phones), never type="number".
 *  - Accepts only: one leading "-", digits, one ".", at most 2 decimals — anything else typed or
 *    pasted is dropped (helper.js sanitizeAmountInput).
 *  - Plain while focused ("1234567.8"); shown as "1,234,567.80" once you leave the field.
 *
 * WHAT IT DOES NOT CHANGE: the value. The screen's own onChange still receives a normal event whose
 * target.value is a plain number string — never a comma — so state, totals and saved payloads are
 * exactly what they were. The comma version exists only in what is painted while the field is not
 * focused. onBlur handlers also still read the plain value, because the field only switches to the
 * formatted display after blur has been handled.
 *
 * A field with no onChange (a computed total, or an old `disabled defaultValue` field) is rendered
 * read-only and always formatted.
 *
 * `plain` renders a native <input> instead of <Form.Control>, for the screens whose amount fields
 * were plain inputs styled with `style={INPUT}` — Form.Control would add Bootstrap's form-control
 * class and change how they look.
 */
export default function AmountInput({ value, defaultValue, onChange, onFocus, onBlur, type, readOnly, plain, ...rest }) {
    const [focused, setFocused] = useState(false);

    const source = value !== undefined ? value : defaultValue;
    const raw = source === null || source === undefined ? "" : String(source);
    const trimmed = raw.trim();
    const isNumber = trimmed !== "" && trimmed !== "-" && Number.isFinite(Number(trimmed));
    // Only a clean number is reformatted; anything else (an old stored "-83,983.68") is shown as is.
    const shown = focused || !isNumber ? raw : formatAmount(trimmed);

    const Field = plain ? "input" : Form.Control;

    return (
        <Field
            {...rest}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={shown}
            readOnly={readOnly || !onChange}
            onFocus={(e) => { setFocused(true); if (onFocus) onFocus(e); }}
            onBlur={(e) => { if (onBlur) onBlur(e); setFocused(false); }}
            onChange={(e) => {
                const clean = sanitizeAmountInput(e.target.value);
                if (clean !== e.target.value) e.target.value = clean;
                if (onChange) onChange(e);
            }}
        />
    );
}
